import { MouseEvent } from 'react';

import { styled } from '@mui/material';
import copy from 'copy-to-clipboard';

import {
  Icons,
  colorOrToken,
  mergeSx,
  SxProps,
  Tooltip,
  TooltipProps,
  notify,
  tokens,
} from '@cast/design-system';

type CopyToClipboardProps = {
  text: string;
  tooltip?: string;
  tooltipProps?: Omit<TooltipProps, 'title' | 'children'>;
  iconSize?: string | number;
  color?: string;
  strokeWidth?: number;
  sx?: SxProps;
  showNotification?: boolean;
  testId?: string;
  onCopy?: (text: string) => void;
};

const StyledIcon = styled(Icons.Copy)({
  fill: 'currentColor',
  cursor: 'pointer',
});

export const CopyToClipboard = ({
  color = colorOrToken('grey.500', tokens.theme.fg.structural.secondary),
  text,
  iconSize = 24,
  tooltip = '',
  tooltipProps = {},
  strokeWidth = 2.5,
  sx,
  showNotification = true,
  testId = 'copy-to-clipboard',
  onCopy,
}: CopyToClipboardProps) => {
  const handleCopy = (e: MouseEvent) => {
    e.stopPropagation();
    copy(text);
    onCopy?.(text);
    if (showNotification) {
      notify.success('Copied to clipboard', { key: 'copy-to-clipboard' });
    }
  };

  const button = (
    <StyledIcon
      data-testid={testId}
      onClick={handleCopy}
      sx={mergeSx(
        {
          color,
          width: '1em',
          height: '1em',
          minWidth: '1em',
          minHeight: '1em',
          fontSize: iconSize,
        },
        sx
      )}
      type="button"
      className="CopyToClipboard-button"
      strokeWidth={strokeWidth}
    />
  );

  if (tooltip) {
    return (
      <Tooltip {...tooltipProps} title={tooltip}>
        {button}
      </Tooltip>
    );
  }

  return <>{button}</>;
};
